import { Button } from '../ui/Button.js'
import { Input } from '../ui/Field.js'
import type { TrackDrawState } from './useTrackDrawState.js'
import { SELECTABLE_ACTIVITIES, isSelectableActivity } from '../../lib/selectableActivities.js'

type TFn = (key: string, options?: Record<string, unknown>) => string

export interface TrackDrawPanelProps {
  draw: TrackDrawState
  t: TFn
  disabled?: boolean
  onFinish: () => void
  onCancel?: () => void
  onDraftChange?: () => void
  finishLabel?: string
  cancelLabel?: string
  showActivity?: boolean
  showRouting?: boolean
  showName?: boolean
}

export function TrackDrawPanel({
  draw,
  t,
  disabled = false,
  onFinish,
  onCancel,
  onDraftChange,
  finishLabel,
  cancelLabel,
  showActivity = false,
  showRouting = true,
  showName = false,
}: TrackDrawPanelProps) {
  const {
    vertices,
    name,
    activities,
    routingEnabled,
    hasDraft,
    stats,
    canFinish,
    setName,
    setActivities,
    setRoutingEnabled,
    deleteVertex,
    moveVertex,
    undo,
    clear,
  } = draw

  const drawHint =
    vertices.length === 0
      ? t('editor.draw.hintEmptyHiking')
      : vertices.length === 1
        ? t('editor.draw.hintOneHiking')
        : t('editor.draw.hintManyHiking', { count: vertices.length })

  return (
    <div className="space-y-3" data-testid="track-draw-panel">
      <p
        className="text-xs leading-relaxed text-[var(--xr-text-muted)]"
        data-testid="drawing-editor-hint"
      >
        {drawHint}
      </p>

      <div className="grid gap-2">
        {showActivity && (
          <fieldset className="flex flex-wrap items-center gap-2">
            {SELECTABLE_ACTIVITIES.map((act) => {
              const checked = activities.includes(act)
              const isLast = activities.length === 1 && checked
              return (
                <label
                  key={act}
                  className="inline-flex items-center gap-1 text-xs cursor-pointer select-none"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={disabled || isLast}
                    onChange={() => {
                      if (disabled || isLast) return
                      onDraftChange?.()
                      const next = checked
                        ? activities.filter((a) => a !== act)
                        : [...activities, act]
                      setActivities(next)
                    }}
                  />
                  <span>{t(`editor.activity${act.charAt(0).toUpperCase() + act.slice(1)}`)}</span>
                </label>
              )
            })}
            {activities
              .filter((act) => !isSelectableActivity(act))
              .map((act) => (
                <span
                  key={act}
                  className="text-xs text-[var(--xr-text-muted)]"
                  title={t('editor.legacyActivity')}
                  data-testid={`drawing-editor-legacy-activity-${act}`}
                >
                  {t(`enumLabel_activity_${act}`)}
                </span>
              ))}
          </fieldset>
        )}
        {showRouting !== false && (
          <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer select-none">
            <input
              type="checkbox"
              checked={routingEnabled}
              onChange={() => {
                onDraftChange?.()
                setRoutingEnabled(!routingEnabled)
              }}
              disabled={disabled}
            />
            <span>{t('editor.draw.routingToggle')}</span>
          </label>
        )}
        {showName && (
          <Input
            data-testid="drawing-editor-name"
            value={name}
            onChange={(event) => {
              onDraftChange?.()
              setName(event.target.value)
            }}
            placeholder={t('editor.draw.namePlaceholder')}
            aria-label={t('editor.draw.namePlaceholder')}
            disabled={disabled}
          />
        )}
      </div>

      <div
        className="rounded-[var(--xr-radius-md)] bg-[var(--xr-bg-surface-elev)] px-3 py-2"
        data-testid="drawing-editor-mode-summary"
      >
        <p className="text-sm font-medium text-[var(--xr-text-primary)]">
          {t('editor.draw.modeHikingTitle')}
        </p>
        <p className="mt-1 text-xs leading-relaxed text-[var(--xr-text-muted)]">
          {t('editor.draw.modeHikingHint')}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3 text-xs text-[var(--xr-text-muted)]">
        <span data-testid="drawing-editor-stats">
          {t('editor.draw.statLineNodes', { count: vertices.length })}
        </span>
        {vertices.length >= 500 && (
          <span className="text-[var(--xr-danger)]">{t('editor.draw.maxLineNodes')}</span>
        )}
        {stats && stats.distanceKm > 0 && (
          <span data-testid="drawing-editor-distance">
            {t('editor.draw.statDistance', { km: stats.distanceKm.toFixed(2) })}
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="drawing-editor-toolbar">
        <Button
          variant="ghost"
          data-testid="drawing-editor-undo"
          onClick={undo}
          disabled={disabled || !hasDraft}
          title={t('editor.draw.undo')}
        >
          {t('editor.draw.undo')}
        </Button>
        <Button
          variant="ghost"
          data-testid="drawing-editor-clear"
          onClick={clear}
          disabled={disabled || !hasDraft}
          title={t('editor.draw.clear')}
        >
          {t('editor.draw.clear')}
        </Button>
        {onCancel && (
          <Button
            variant="secondary"
            data-testid="drawing-editor-cancel"
            onClick={onCancel}
            disabled={disabled}
          >
            {cancelLabel ?? t('editor.draw.cancel')}
          </Button>
        )}
        <Button
          data-testid="drawing-editor-finish"
          onClick={onFinish}
          disabled={disabled || !canFinish}
          title={
            canFinish ? (finishLabel ?? t('editor.draw.finish')) : t('editor.draw.minHikingPoints')
          }
        >
          {finishLabel ?? t('editor.draw.finish')}
        </Button>
      </div>

      <div className="space-y-3">
        {vertices.length > 0 && (
          <div className="space-y-2">
            {vertices.map((vertex, index) => (
              <div
                key={`${vertex.lng},${vertex.lat},${index}`}
                className="grid gap-2 md:grid-cols-[1fr_auto]"
              >
                <span className="rounded-[var(--xr-radius-sm)] bg-[var(--xr-bg-surface-elev)] px-3 py-2 text-sm text-[var(--xr-text-secondary)]">
                  {t('editor.draw.lineNodeName', { count: index + 1 })} · {vertex.lat.toFixed(5)},{' '}
                  {vertex.lng.toFixed(5)}
                </span>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    onClick={() => moveVertex(index, -1)}
                    disabled={disabled || index === 0}
                  >
                    {t('editor.draw.moveUp')}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => moveVertex(index, 1)}
                    disabled={disabled || index === vertices.length - 1}
                  >
                    {t('editor.draw.moveDown')}
                  </Button>
                  <Button variant="ghost" onClick={() => deleteVertex(index)} disabled={disabled}>
                    {t('editor.draw.deletePoint')}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
